import type { LightSource } from '../../shared/lighting.ts';
import type { Direction, Position } from '../../shared/protocol.ts';
import type { Entity } from './entity.ts';
import type { BattleScene, WorldRenderer } from './world-renderer.ts';

/**
 * Combat sur la carte : pas d'écran à part, l'arène se pose dans le monde, là où le joueur se tient. La caméra glisse
 * vers elle et zoome, un cercle se trace au sol, le {monstre} suiveur bondit dans l'arène et l'adversaire surgit face
 * au joueur ; le monde continue de vivre autour (joueurs, météo, nuit), se fige aux arrêts sur image et tremble aux coups.
 */

/** Côté où se tient l'adversaire : à droite ou à gauche du joueur (légèrement plus haut), ou devant lui, vers le haut. */
type FormationKind = 'right' | 'left' | 'up';

/**
 * Pied de chaque monstre, en cases depuis le pied du joueur (milieu du bas de sa case), et celui du dresseur adverse
 * (PNJ ou joueur en duel), qui se tient en face du joueur, derrière son monstre.
 */
interface Layout {
  ally: Position;
  foe: Position;
  opponent: Position;
}

/**
 * Placements possibles, du préféré au dernier recours : on prend le premier où aucun monstre ne cache un personnage (le
 * dresseur venu défier le joueur se tient souvent juste devant lui). Ceux de gauche sont ceux de droite, en miroir.
 */
const LAYOUTS: Readonly<Record<'right' | 'up', readonly Layout[]>> = {
  right: [
    { ally: { x: 1.4, y: 0.15 }, foe: { x: 5.3, y: -0.5 }, opponent: { x: 7.1, y: -0.9 } },
    { ally: { x: 0.35, y: 1.5 }, foe: { x: 5.1, y: 0.3 }, opponent: { x: 6.9, y: -0.1 } },
    { ally: { x: 0.35, y: 1.5 }, foe: { x: 4.6, y: -2 }, opponent: { x: 6.4, y: -2.4 } },
  ],
  up: [
    { ally: { x: 1.25, y: 0.1 }, foe: { x: 2, y: -3.5 }, opponent: { x: 3.6, y: -4.2 } },
    { ally: { x: -1.25, y: 0.1 }, foe: { x: -0.6, y: -3.5 }, opponent: { x: 1, y: -4.2 } },
    { ally: { x: 1.25, y: 0.1 }, foe: { x: 2.4, y: -4.6 }, opponent: { x: 4, y: -5.3 } },
  ],
};

/** Vers le bas, l'adversaire serait entre le joueur et la caméra : le joueur se tourne plutôt sur le côté. */
const FORMATION_OF: Readonly<Record<Direction, FormationKind>> = { up: 'up', down: 'right', left: 'left', right: 'right' };
const FACING_OF: Readonly<Record<FormationKind, Direction>> = { up: 'up', right: 'right', left: 'left' };
/** Le dresseur adverse regarde le joueur. */
const OPPONENT_FACING: Readonly<Record<FormationKind, Direction>> = { up: 'down', right: 'left', left: 'right' };

/** Emprise d'un monstre au sol, en cases (largeur, hauteur au-dessus du pied), et marge tolérée sur un personnage. */
const FOOTPRINT = { width: 1.4, height: 1.5, margin: 0.3 } as const;

/** Ouverture et fermeture de l'arène, en ms ; zoom de la caméra une fois posée. */
const ENTER_MS = 750;
const LEAVE_MS = 550;
const ZOOM = 1.1;
/** Taille d'affichage des monstres, en cases. */
const SPRITE_CELLS = 2.3;
/** Secousse de la caméra aux coups : durée de l'amortissement. */
const SHAKE_MS = 320;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

/** Dresseur adverse déplacé en face du joueur le temps du combat : d'où il part, et sa direction à lui rendre ensuite. */
interface Opponent {
  entity: Entity;
  from: Position;
  dir: Direction;
}

/** Places utilisées de chaque camp (2 : combat double). */
export interface FieldPlaces {
  allies: number;
  foes: number;
}

/**
 * Combat double : décalage de chaque place par rapport à la place unique d'un combat simple. Côte à côte vers le haut
 * (les alliés s'écartent du joueur), l'un derrière l'autre sur le côté (la place 1, plus bas, passe devant).
 */
const SPREAD: Readonly<Record<'side' | 'up', { ally: readonly Position[]; foe: readonly Position[] }>> = {
  side: { ally: [{ x: 0.3, y: -0.75 }, { x: -0.3, y: 0.75 }], foe: [{ x: 0.3, y: -0.75 }, { x: -0.3, y: 0.75 }] },
  up: { ally: [{ x: 0, y: 0 }, { x: 1.45, y: 0.15 }], foe: [{ x: -0.8, y: 0 }, { x: 0.8, y: 0.1 }] },
};

/** Les places d'un camp autour de la place unique `base` (`mirror` : -1 à gauche ; `away` : signe de l'écart au joueur). */
function spread(base: Position, count: number, offsets: readonly Position[], mirror: number, away = 1): Position[] {
  if (count <= 1) return [base];
  return offsets.slice(0, count).map((offset) => ({ x: base.x + offset.x * mirror * away, y: base.y + offset.y }));
}

interface Formation {
  kind: FormationKind;
  /** Pied de chaque monstre, par place. */
  allies: Position[];
  foes: Position[];
  opponent: Position;
  /** Null contre un monstre sauvage. */
  rival: Opponent | null;
  /** Pied du joueur (milieu du bas de sa case), d'où part la capsule. */
  trainer: Position;
  /** Pied du suiveur, d'où bondit le premier monstre envoyé ; null sans suiveur. */
  follower: Position | null;
}

/** Les deux rectangles se chevauchent (au-delà de la marge tolérée). */
const overlaps = (a: { x0: number; y0: number; x1: number; y1: number }, b: typeof a) =>
  Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0) > FOOTPRINT.margin && Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0) > FOOTPRINT.margin;

/** Emprise d'un monstre dont le pied est en `foot`. */
const footprint = (foot: Position) => ({ x0: foot.x - FOOTPRINT.width / 2, y0: foot.y - FOOTPRINT.height, x1: foot.x + FOOTPRINT.width / 2, y1: foot.y });
/** Place d'un personnage sur sa case, en comptant sa tête qui dépasse au-dessus. */
const standing = ({ x, y }: Position) => ({ x0: x, y0: y - 0.6, x1: x + 1, y1: y + 1 });

/** Ordre d'essai des côtés : celui où regarde le joueur, puis l'autre côté, puis devant lui (ou l'inverse). */
const SIDES_TO_TRY: Readonly<Record<FormationKind, readonly FormationKind[]>> = {
  right: ['right', 'left', 'up'], left: ['left', 'right', 'up'], up: ['up', 'right', 'left'],
};

/** Centre et rayons du cercle au sol (en cases) autour de tous les monstres. */
function ringOf(feet: readonly Position[]): { x: number; y: number; rx: number; ry: number } {
  const xs = feet.map((foot) => foot.x), ys = feet.map((foot) => foot.y);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2, rx: Math.max(2.4, (x1 - x0) / 2 + 1.5), ry: Math.max(1.3, (y1 - y0) / 2 + 0.9) };
}

/**
 * Choisit l'emplacement de l'arène autour du joueur : du côté où il regarde si possible, sinon de l'autre, sinon devant
 * lui, en gardant toute l'arène (cercle, monstres, dresseur adverse) dans la carte et sans cacher les personnages autour
 * (le dresseur adverse n'en fait pas partie : il vient se placer en face). `size` : largeur et hauteur de la carte.
 */
function formationFor(self: Entity, occupied: readonly Position[], rival: Entity | null, size: { width: number; height: number }, places: FieldPlaces): Formation {
  const trainer = { x: self.x + 0.5, y: self.y + 1 };
  const people = [{ x: self.x, y: self.y }, ...occupied].map(standing);
  const candidates = SIDES_TO_TRY[FORMATION_OF[self.dir]].flatMap((kind) => {
    const mirror = kind === 'left' ? -1 : 1;
    const place = (offset: Position) => ({ x: trainer.x + offset.x * mirror, y: trainer.y + offset.y });
    const offsets = SPREAD[kind === 'up' ? 'up' : 'side'];
    return LAYOUTS[kind === 'up' ? 'up' : 'right'].map(({ ally, foe, opponent }) => ({
      kind,
      allies: spread(place(ally), places.allies, offsets.ally, mirror, kind === 'up' ? Math.sign(ally.x) || 1 : 1),
      foes: spread(place(foe), places.foes, offsets.foe, mirror),
      opponent: place(opponent),
    }));
  });
  const inside = (box: { x0: number; y0: number; x1: number; y1: number }) => box.x0 >= 0 && box.y0 >= 0 && box.x1 <= size.width && box.y1 <= size.height;
  const fits = ({ allies, foes, opponent }: (typeof candidates)[number]) => {
    const ring = ringOf([...allies, ...foes]);
    const rivalBox = standing({ x: opponent.x - 0.5, y: opponent.y - 1 });
    return inside({ x0: ring.x - ring.rx, y0: ring.y - ring.ry - FOOTPRINT.height, x1: ring.x + ring.rx, y1: ring.y + ring.ry })
      && [...allies, ...foes].map(footprint).concat(rival ? [rivalBox] : []).every(inside);
  };
  const clear = ({ allies, foes, opponent }: (typeof candidates)[number]) => people.every((person) =>
    [...allies, ...foes].every((foot) => !overlaps(footprint(foot), person)) && (!rival || !overlaps(standing({ x: opponent.x - 0.5, y: opponent.y - 1 }), person)));
  const chosen = candidates.find((candidate) => fits(candidate) && clear(candidate)) ?? candidates.find(fits) ?? candidates[0]!;
  const { trail } = self;
  return {
    ...chosen, trainer,
    rival: rival && { entity: rival, from: { x: rival.renderX, y: rival.renderY }, dir: rival.dir },
    follower: self.follower ? { x: trail.x + 0.5, y: trail.y + 0.95 } : null,
  };
}

/** Lumière d'un monstre au combat, la nuit : un halo de la couleur de son type. */
const monsterLight = (color: string): LightSource => ({ radius: 2.6, intensity: 0.85, color, flicker: 0.4 });

export class BattleField {
  private readonly root: HTMLElement;
  private readonly arena: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private formation: Formation | null = null;
  /** Direction du joueur avant le combat, rendue à la fin (le serveur ne l'a jamais vu se tourner). */
  private selfFacing: { entity: Entity; dir: Direction } | null = null;
  private openedAt = 0;
  private closedAt: number | null = null;
  /** Ouverture au moment de la fermeture (un combat qui se termine avant d'être tout à fait ouvert). */
  private closedFrom = 1;
  /** Hauteur de l'écran où poser l'arène : au milieu de la partie visible, au-dessus du panneau des commandes. */
  private screenY = innerHeight / 2;
  private shakePx = 0;
  private shakeAt = 0;
  /** Couleur du type du monstre à chaque place (« ally0 », « foe1 »…), pour le cercle au sol et les halos. */
  private readonly colors = new Map<string, string>();

  constructor(root: HTMLElement, canvas: HTMLCanvasElement) {
    this.root = root;
    this.canvas = canvas;
    this.arena = root.querySelector<HTMLElement>('.arena')!;
    this.panel = root.querySelector<HTMLElement>('.panel')!;
    root.addEventListener('battle-shake', (event) => {
      this.shakePx = Math.max(this.shakeAt + SHAKE_MS > performance.now() ? this.shakePx : 0, (event as CustomEvent<number>).detail);
      this.shakeAt = performance.now();
    });
  }

  /** Une arène est posée (ou en train de se replier). */
  get active(): boolean {
    return this.formation !== null;
  }

  /**
   * Le combat commence : l'arène se pose autour du joueur, qui se tourne vers l'adversaire ; le dresseur adverse (PNJ,
   * joueur en duel ; null contre un monstre sauvage) vient se placer en face. `occupied` : personnages à ne pas cacher
   * derrière un monstre.
   */
  begin(self: Entity, occupied: readonly Entity[], rival: Entity | null, size: { width: number; height: number }, now: number, places: FieldPlaces): void {
    this.restoreRival();
    const formation = formationFor(self, occupied.filter((entity) => entity !== rival), rival, size, places);
    this.formation = formation;
    this.selfFacing = { entity: self, dir: self.dir };
    self.dir = FACING_OF[formation.kind];
    if (rival) rival.dir = OPPONENT_FACING[formation.kind];
    this.openedAt = now;
    this.closedAt = null;
    this.arena.dataset.formation = formation.kind;
    this.arena.classList.toggle('mirror', formation.kind === 'left');
  }

  /**
   * Le combat est fini : l'arène se replie et la caméra revient sur le joueur, qui reprend sa direction d'avant (sur
   * la glace, sa glissade repart dans le même sens).
   */
  end(now: number): void {
    if (!this.formation || this.closedAt !== null) return;
    if (this.selfFacing) this.selfFacing.entity.dir = this.selfFacing.dir;
    this.selfFacing = null;
    this.closedFrom = this.openingAt(now);
    this.closedAt = now;
  }

  /**
   * Couleurs des types des monstres au combat, par place (« ally0 », « foe1 »…) : le cercle au sol prend celle du
   * premier adversaire, leurs halos la leur.
   */
  setColors(colors: Readonly<Record<string, string | null>>): void {
    for (const [slot, color] of Object.entries(colors)) if (color) this.colors.set(slot, color);
  }

  /** Ouverture de l'arène, de 0 (fermée) à 1 (posée). */
  private openingAt(now: number): number {
    if (this.closedAt === null) return Math.min(1, (now - this.openedAt) / ENTER_MS);
    return Math.max(0, this.closedFrom - (now - this.closedAt) / LEAVE_MS);
  }

  /** Ce que le monde doit dessiner pour l'arène à cet instant (undefined : pas de combat). */
  scene(now: number): BattleScene | undefined {
    const formation = this.formation;
    if (!formation) return undefined;
    const amount = this.openingAt(now);
    if (this.closedAt !== null && amount <= 0) {
      this.restoreRival();
      this.formation = null;
      this.canvas.style.transform = '';
      return undefined;
    }
    const { allies, foes, rival, opponent } = formation;
    // Le dresseur adverse glisse jusqu'à sa place pendant l'ouverture de l'arène, et revient à la fermeture.
    const t = easeInOut(amount);
    const stand = rival && {
      entity: rival.entity,
      x: rival.from.x + (opponent.x - 0.5 - rival.from.x) * t,
      y: rival.from.y + (opponent.y - 1 - rival.from.y) * t,
    };
    const ring = ringOf([...allies, ...foes]);
    const color = (slot: string) => this.colors.get(slot) ?? '#ffffff';
    return {
      amount,
      focus: { x: ring.x, y: ring.y - SPRITE_CELLS * 0.45 },
      screenY: this.screenY,
      ring: { ...ring, y: ring.y - 0.1, color: color('foe0') },
      lights: [
        ...allies.map((foot, place) => ({ x: foot.x, y: foot.y - 0.8, light: monsterLight(color(`ally${place}`)) })),
        ...foes.map((foot, place) => ({ x: foot.x, y: foot.y - 0.8, light: monsterLight(color(`foe${place}`)) })),
      ],
      ...(stand ? { stand } : {}),
    };
  }

  /** Le dresseur adverse reprend la direction qu'il avait avant le combat (sa position, elle, n'a jamais changé). */
  private restoreRival(): void {
    const rival = this.formation?.rival;
    if (rival) rival.entity.dir = rival.dir;
  }

  /**
   * Après le dessin du monde : zoome la caméra (le canvas entier, agrandi sans flou) et place les monstres, la capsule
   * et le halo de l'écran de combat sur leurs points de la carte.
   */
  place(renderer: WorldRenderer, now: number, scene: BattleScene | undefined): void {
    const formation = this.formation;
    if (!formation || !scene) return;
    if (!this.root.hidden) {
      const top = this.panel.getBoundingClientRect().top;
      if (top > 0) {
        // Au milieu de la partie visible : les fiches se placent autour des monstres, de tous les côtés.
        this.screenY = Math.max(innerHeight * 0.22, top / 2);
        this.root.style.setProperty('--panel-top', `${top.toFixed(0)}px`);
      }
    }
    const zoom = 1 + (ZOOM - 1) * easeInOut(scene.amount);
    const origin = { x: innerWidth / 2, y: innerHeight / 2 + (this.screenY - innerHeight / 2) * easeInOut(scene.amount) };
    const elapsed = now - this.shakeAt;
    const shake = elapsed < SHAKE_MS ? this.shakePx * (1 - elapsed / SHAKE_MS) : 0;
    const jitter = { x: (Math.random() - 0.5) * 2 * shake, y: (Math.random() - 0.5) * 2 * shake };
    this.canvas.style.transformOrigin = `${origin.x}px ${origin.y}px`;
    this.canvas.style.transform = zoom === 1 && !shake ? '' : `translate(${jitter.x.toFixed(1)}px, ${jitter.y.toFixed(1)}px) scale(${zoom})`;
    if (this.root.hidden) return;

    const screen = (point: Position) => {
      const raw = renderer.project(point.x, point.y);
      return { x: origin.x + (raw.x - origin.x) * zoom + jitter.x, y: origin.y + (raw.y - origin.y) * zoom + jitter.y };
    };
    const cell = renderer.cell * zoom;
    const style = this.root.style;
    style.setProperty('--cell', `${cell.toFixed(1)}px`);
    style.setProperty('--sprite', `${(cell * SPRITE_CELLS).toFixed(1)}px`);
    style.setProperty('--world-dark', renderer.ambientDarkness.toFixed(2));
    const focus = screen(scene.focus);
    style.setProperty('--fx', `${focus.x.toFixed(0)}px`);
    style.setProperty('--fy', `${focus.y.toFixed(0)}px`);
    const put = (element: HTMLElement | null, at: Position) => {
      element?.style.setProperty('--x', `${at.x.toFixed(1)}px`);
      element?.style.setProperty('--y', `${at.y.toFixed(1)}px`);
    };
    // Décalage d'un point à un autre, en deux variables (`--nom-x`, `--nom-y`) : les trajectoires en cloche les combinent.
    const offset = (element: HTMLElement | null, name: string, from: Position, to: Position) => {
      element?.style.setProperty(`${name}-x`, `${(from.x - to.x).toFixed(1)}px`);
      element?.style.setProperty(`${name}-y`, `${(from.y - to.y).toFixed(1)}px`);
    };
    const trainer = screen({ x: formation.trainer.x, y: formation.trainer.y - 0.6 });
    const cards: HTMLElement[] = [];
    // Le premier monstre bondit depuis le suiveur ; les suivants (et la capsule) partent de la main du joueur, et y
    // reviennent quand il s'enfuit.
    const follower = formation.follower ? screen(formation.follower) : null;
    formation.allies.forEach((foot, place) => {
      const mine = this.root.querySelector<HTMLElement>(`.side.mine[data-place="${place}"]`);
      const at = screen(foot);
      put(mine, at);
      offset(mine, '--enter-from', place === 0 && follower ? follower : trainer, at);
      offset(mine, '--leave-to', trainer, at);
      const card = mine?.querySelector<HTMLElement>('.info');
      if (card && mine?.style.visibility === 'visible') cards.push(card);
      // Une fois le premier monstre entré (son bond fini), les suivants viennent de la main du joueur.
      if (place === 0 && mine?.style.visibility === 'visible' && !mine.querySelector('.sprite.enter-ally')) formation.follower = null;
    });
    const foes = formation.foes.map(screen);
    foes.forEach((at, place) => {
      const wild = this.root.querySelector<HTMLElement>(`.side.wild[data-place="${place}"]`);
      put(wild, at);
      const card = wild?.querySelector<HTMLElement>('.info');
      if (card && wild?.style.visibility === 'visible') cards.push(card);
    });
    const capsule = this.root.querySelector<HTMLElement>('.capsule');
    const aimed = foes[Number(capsule?.dataset.place ?? 0)] ?? foes[0];
    if (aimed) {
      put(capsule, aimed);
      offset(capsule, '--throw-from', trainer, aimed);
    }
    // Le joueur et le dresseur adverse ne doivent pas disparaître derrière une fiche.
    const person = (foot: Position) => {
      const at = screen(foot);
      return { left: at.x - cell * 0.5, top: at.y - cell * 1.5, right: at.x + cell * 0.5, bottom: at.y };
    };
    const people = [person(formation.trainer), ...(formation.rival ? [person(formation.opponent)] : [])];
    this.fitCards(cards, people);
  }

  /**
   * Fiches des monstres, chacune collée à son monstre, du côté le plus éloigné du centre de l'arène (au-dessus, en
   * dessous, à gauche ou à droite) parmi ceux qui sont libres : dans l'écran, au-dessus du panneau des commandes, sans
   * chevaucher une autre fiche ni cacher les autres monstres ou les dresseurs (`people`). Toutes les combinaisons de
   * côtés sont essayées ; faute de combinaison qui respecte tout, c'est celle qui cache le moins de choses qui l'emporte.
   */
  private fitCards(cards: readonly HTMLElement[], people: readonly { left: number; top: number; right: number; bottom: number }[]): void {
    if (!cards.length) return;
    const edge = 8, gap = 4;
    const bounds = { left: edge, top: edge, right: innerWidth - edge, bottom: this.panel.getBoundingClientRect().top - edge };
    const sides = cards.map((card) => {
      const anchor = card.parentElement!.getBoundingClientRect();
      const pedestal = card.parentElement!.querySelector('.pedestal')!.getBoundingClientRect();
      // Les images des monstres ont des marges transparentes : le monstre lui-même est un peu plus petit que son socle.
      const sprite = new DOMRect(pedestal.left + pedestal.width * 0.15, pedestal.top + pedestal.height * 0.12, pedestal.width * 0.7, pedestal.height * 0.88);
      const size = card.getBoundingClientRect();
      return { card, anchor, sprite, width: size.width, height: size.height };
    });
    const center = {
      x: sides.reduce((sum, side) => sum + side.sprite.x + side.sprite.width / 2, 0) / sides.length,
      y: sides.reduce((sum, side) => sum + side.sprite.y + side.sprite.height / 2, 0) / sides.length,
    };
    type Box = { left: number; top: number; right: number; bottom: number };
    const box = (left: number, top: number, width: number, height: number): Box => ({ left, top, right: left + width, bottom: top + height });
    const clamp = (value: number, min: number, max: number) => (max < min ? min : Math.min(max, Math.max(min, value)));
    const hits = (a: Box, b: Box) => Math.min(a.right, b.right) - Math.max(a.left, b.left) > 0 && Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > 0;
    const inside = (b: Box) => b.left >= bounds.left - 0.5 && b.right <= bounds.right + 0.5 && b.top >= bounds.top - 0.5 && b.bottom <= bounds.bottom + 0.5;
    // Les quatre places autour de chaque monstre, glissées le long de leur côté pour rester dans l'écran ; la plus loin
    // du centre de l'arène a le meilleur score (un petit bonus pour « au-dessus », la place habituelle).
    const candidates = sides.map(({ sprite, width, height }) => {
      const midX = sprite.x + sprite.width / 2 - width / 2, midY = sprite.y + sprite.height / 2 - height / 2;
      const alongX = clamp(midX, bounds.left, bounds.right - width), alongY = clamp(midY, bounds.top, bounds.bottom - height);
      return [
        { place: box(alongX, sprite.top - gap - height, width, height), bonus: 12 },
        { place: box(alongX, sprite.bottom + gap, width, height), bonus: 0 },
        { place: box(sprite.left - gap - width, alongY, width, height), bonus: 0 },
        { place: box(sprite.right + gap, alongY, width, height), bonus: 0 },
      ].map(({ place, bonus }) => ({
        place,
        score: Math.hypot((place.left + place.right) / 2 - center.x, (place.top + place.bottom) / 2 - center.y) + bonus,
      }));
    });
    const spriteBoxes = sides.map(({ sprite }) => box(sprite.x, sprite.y, sprite.width, sprite.height));
    /** Surface de `a` qui couvre `b`, en pixels carrés. */
    const covered = (a: Box, b: Box) => Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left)) * Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
    let best = null as { places: Box[]; score: number } | null;
    // Dans l'écran et sans se chevaucher (puis, faute de mieux, sans condition) ; parmi ces combinaisons, celle qui cache
    // le moins les autres monstres et les dresseurs, puis la plus éloignée du centre.
    for (const strict of [true, false]) {
      const chosen: { place: Box; score: number }[] = [];
      const search = (index: number): void => {
        if (index === sides.length) {
          let score = 0;
          chosen.forEach(({ place, score: own }, i) => {
            score += own - spriteBoxes.reduce((sum, sprite, j) => sum + (i === j ? 0 : covered(place, sprite)), 0)
              - people.reduce((sum, person) => sum + covered(place, person), 0);
            if (!strict) for (let j = i + 1; j < chosen.length; j++) score -= covered(place, chosen[j]!.place) * 4;
          });
          if (!best || score > best.score) best = { places: chosen.map(({ place }) => place), score };
          return;
        }
        for (const candidate of candidates[index]!) {
          if (strict && (!inside(candidate.place) || chosen.some(({ place }) => hits(place, candidate.place)))) continue;
          chosen.push(candidate);
          search(index + 1);
          chosen.pop();
        }
      };
      search(0);
      if (best) break;
    }
    if (!best) return;
    const { places } = best;
    sides.forEach((side, index) => {
      const place = places[index]!;
      // Dernier recours (contraintes relâchées) : la fiche est tout de même ramenée dans l'écran.
      const left = clamp(place.left, bounds.left, bounds.right - side.width), top = clamp(place.top, bounds.top, bounds.bottom - side.height);
      side.card.style.setProperty('--card-x', `${(left - side.anchor.left).toFixed(1)}px`);
      side.card.style.setProperty('--card-y', `${(top - side.anchor.top).toFixed(1)}px`);
      side.card.classList.add('placed');
    });
  }
}
